export function postConfigurationSavedToParent(payload: {
    recordId?: string;
    customerId?: string;
    name: string;
    productName?: string;
    qtySelected?: string | number;
    screenshots: { label: string; dataUrl: string }[];
    /**
     * VDP (publipostage) : nombre de lignes de variation de texte de la création
     * (0 si pas de VDP). Remonté au storefront e-commerce pour la facturation du
     * surcoût VDP au client final (propriété `textvdp` de la ligne de panier).
     */
    textvdp?: number;
    /**
     * Encres/fils choisis (sérigraphie / broderie) : tableau { name, pantone, hex }.
     * Vide en numérique/laser. Remonté au storefront (facturation par couleur) et
     * affiché dans le BAT sous l'aplat 2D.
     */
    selectedInks?: Array<{ name: string; pantone: string | null; hex: string }>;
}) {
    if (typeof window === 'undefined' || window.parent === window) {
        return;
    }
    const message: Record<string, unknown> = {
        eventType: 'configurationSaved',
        Name: payload.name,
        // Toujours présent (0 si pas de VDP) pour que le storefront puisse tarifer.
        textvdp: Math.max(0, Math.floor(payload.textvdp ?? 0)),
        // Toujours présent (tableau vide si numérique/laser).
        selectedInks: Array.isArray(payload.selectedInks) ? payload.selectedInks : [],
        Screenshots: payload.screenshots.map(({ label, dataUrl }) => ({ label, dataUrl }))
    };
    if (payload.recordId) {
        message.RecordID = payload.recordId;
    }
    if (payload.customerId) {
        message.CustomerID = payload.customerId;
    }
    if (payload.productName) {
        message.productName = payload.productName;
    }
    if (payload.qtySelected !== undefined && payload.qtySelected !== null && payload.qtySelected !== '') {
        message.qtyselected = payload.qtySelected;
    }
    window.parent.postMessage(JSON.stringify(message), '*');
}

export function usePostMessage() {
    function askEcommerceProduct(handle: string) {
        const message = {
            eventType: 'askEcommerceProduct',
            handle
        };
        const jsonMessage = JSON.stringify(message);
        window.parent.postMessage(jsonMessage, '*');
    }

    function askEcommerceCustomerId() {
        const message = {
            eventType: 'askCustomerId'
        };
        const jsonMessage = JSON.stringify(message);
        window.parent.postMessage(jsonMessage, '*');
    }

    /**
     * Demande à l'HÔTE (CMS) de connecter le client. L'auth (formulaire, SSO Google,
     * redirection) appartient au storefront — le configurateur ne gère JAMAIS le login
     * (iframe tierce : cookies/SSO bloqués). L'hôte, une fois le client connecté,
     * ré-émet `sendCustomerId` → le configurateur reprend l'action en attente
     * (sauvegarde). Contrat portable Medusa/Shopify/PrestaShop.
     * Cf. docs/integration-comptes-sauvegardes-cms.md.
     */
    function requireLogin(reason: 'save' | string = 'save') {
        if (typeof window === 'undefined' || window.parent === window) {
            return;
        }
        window.parent.postMessage(JSON.stringify({ eventType: 'requireLogin', reason }), '*');
    }

    function askRedirect(url: string) {
        const message = {
            eventType: 'redirect',
            url: url
        };
        window.parent.postMessage(JSON.stringify(message), '*');
    }

    function askVariantsData(variantRefs: string[], productHandle?: string) {
        const message = {
            eventType: 'askVariantsData',
            variantRefs,
            productHandle
        };

        const jsonMessage = JSON.stringify(message);
        window.parent.postMessage(jsonMessage, '*');
    }

    function askParentUrl() {
        const message = {
            eventType: 'askParentUrl'
        };
        const jsonMessage = JSON.stringify(message);
        window.parent.postMessage(jsonMessage, '*');
    }

    /**
     * Demande à l'hôte les PRIX MINI d'une liste de produits catalogue, en UN seul
     * message (le FO ne pouvant pas pricer sans ouvrir chaque produit ni sans le
     * mapping variante→méthode qui vit côté hôte). L'hôte répond `sendCatalogPrices`.
     * Contrat : cf. docs/integration-comptes-sauvegardes-cms.md (§ prix tunnel).
     *   host → FO : { eventType:'sendCatalogPrices',
     *     prices: { [productId]: { from:{price,qty}, byMethod:{ [printProfileId]:{price,qty} } } | null } }
     * null/absent = pas de prix → produit masqué. byMethod absent = méthode masquée.
     */
    function askCatalogPrices(productIds: string[]) {
        if (typeof window === 'undefined' || window.parent === window || !productIds.length) {
            return;
        }
        window.parent.postMessage(JSON.stringify({ eventType: 'askCatalogPrices', productIds }), '*');
    }

    return {
        askEcommerceProduct,
        askEcommerceCustomerId,
        askRedirect,
        askVariantsData,
        askParentUrl,
        askCatalogPrices,
        requireLogin
    };
}
